import React from 'react'
import { Link } from 'react-router-dom'
import { LocationDescriptor } from 'history'

import { PublicationItemFragment } from '@sketch/gql-types'

import { Text, TimeAgo, useResponsiveDropdown } from '@sketch/components'

import { useFlag, useTrackEvent } from '@sketch/modules-common'

import { ReactComponent as DocumentIcon } from '@sketch/icons/document-17'
import { ReactComponent as LibraryIcon } from '@sketch/icons/link-library-17'
import { ReactComponent as TemplateIcon } from '@sketch/icons/templates-17'

import {
  Container,
  HeartButton,
  Icon,
  Left,
  Name,
  PublicationThumbnail,
  Right,
  TitleWrapper,
  DropdownButton,
  DropdownButtonEllipsis,
  DropdownButtonWrapper,
  UpdatedAt,
} from './GridPublicationItem.styles'
import { useDuplicateShareToDrafts } from '../../../shares/hooks/useDuplicateShareToDrafts'
import { PublicationActionsDropdown } from '../PublicationActionsDropdown'

const ICON_BY_TYPE = {
  STANDARD: DocumentIcon,
  LIBRARY: LibraryIcon,
  TEMPLATE: TemplateIcon,
}
export interface GridPublicationItemProps {
  className?: string
  publication: PublicationItemFragment
  url?: LocationDescriptor
  showProfile?: boolean
}

export const GridPublicationItem = (props: GridPublicationItemProps) => {
  const { className, publication, url, showProfile } = props

  // Analytics
  const { trackEvent } = useTrackEvent()

  const isHeartButtonAvailable = useFlag('publication-hearts')

  const publicationName = publication.share.name
  const share = publication.share
  const workspace = share.workspace

  const workspaceName = workspace.name
  const updatedAt = share.version?.createdAt ?? publication.updatedAt
  const shareType = share.shareType
  const isLibrary = shareType === 'LIBRARY'
  const IconByType = ICON_BY_TYPE[shareType]

  const [duplicateShare, { loading: isDuplicating }] =
    useDuplicateShareToDrafts({
      share,
      versionIdentifier: share.version?.identifier || '',
      workspaceIdentifier: workspace.identifier,
    })

  const [dropdown, buttonProps] = useResponsiveDropdown({
    dropdown: PublicationActionsDropdown,
    dropdownProps: {
      publication,
      workspace,
      onDuplicate: duplicateShare,
      isDuplicating,
    },
    placement: 'bottom-end',
    usePortal: true,
  })

  const handleClick = () => {
    trackEvent('COMMUNITY - Publication', {
      type: 'Clicks - Publication Grid Item',
      target: share.name,
    })
  }

  const content = (
    <>
      <PublicationThumbnail
        previewFile={publication.previewFile}
        sharePreviewFile={share.version?.document?.previewFile}
        name={publicationName}
      />
      <TitleWrapper>
        <Left>
          <Icon $isLibrary={isLibrary} as={IconByType} width="17" height="17" />
          <Name>{publicationName}</Name>
        </Left>
        <Right>
          <UpdatedAt>
            <TimeAgo date={updatedAt} />
          </UpdatedAt>
        </Right>
        {isHeartButtonAvailable && (
          <HeartButton
            publication={publication}
            flavour="list"
            trackHeartCount
          />
        )}
      </TitleWrapper>
      {showProfile && (
        <Text
          textStyle="copy.quaternary.standard.C"
          as="span"
          overflow="ellipsis"
        >
          {workspaceName}
        </Text>
      )}
    </>
  )

  if (url) {
    return (
      <Link className={className} to={url} onClick={handleClick}>
        <Container title={publicationName}>
          <DropdownButtonWrapper>
            <DropdownButton
              {...buttonProps}
              onClick={(e: React.MouseEvent<HTMLElement>) => {
                e.preventDefault()
                e.stopPropagation()
                if (buttonProps.onClick) {
                  buttonProps.onClick(e)
                }
              }}
            >
              <DropdownButtonEllipsis aria-label="Publication Settings" />
            </DropdownButton>
            {dropdown}
          </DropdownButtonWrapper>
          {content}
        </Container>
      </Link>
    )
  }

  return (
    <Container className={className} title={publicationName}>
      <DropdownButtonWrapper>
        <DropdownButton {...buttonProps}>
          <DropdownButtonEllipsis aria-label="Publication Settings" />
        </DropdownButton>
        {dropdown}
      </DropdownButtonWrapper>
      {content}
    </Container>
  )
}
